import { useState } from "react";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AdminStatusBadge } from "@/admin/components/AdminStatusBadge";
import { ReasonModal } from "@/admin/components/ReasonModal";
import { useVerifications } from "@/admin/hooks/useVerifications";
import type { VerificationStatus, VerificationQueueItem } from "@/admin/types/admin";
import { VERIFICATION_STATUS_LABELS } from "@/admin/types/admin";

const FILTERS: VerificationStatus[] = ["pending", "verified", "rejected"];
const FILTER_ALL = "all";

export default function AdminVerifications() {
  const [filter, setFilter] = useState<VerificationStatus | typeof FILTER_ALL>(FILTER_ALL);
  const [rejectTarget, setRejectTarget] = useState<VerificationQueueItem | null>(null);

  const effectiveFilter = filter === FILTER_ALL ? undefined : filter;
  const { query, approveMutation, rejectMutation } = useVerifications(effectiveFilter);

  const items = query.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Vérifications artisans</h1>
        <p className="text-sm text-gray-500 mt-1">{items.length} résultat(s)</p>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setFilter(FILTER_ALL)}
          className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
            filter === FILTER_ALL
              ? "bg-primary text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          Tous
        </button>
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              filter === f
                ? "bg-primary text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            {VERIFICATION_STATUS_LABELS[f]}
          </button>
        ))}
      </div>

      {/* Table */}
      {query.isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center">
          <p className="text-gray-400">Aucune vérification trouvée</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Artisan</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Spécialité</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Wilaya</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Date demande</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Statut</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((item) => (
                <tr key={item.artisanProfileId} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{item.fullName}</p>
                    <p className="text-xs text-gray-500">{item.email}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{item.specialty}</td>
                  <td className="px-4 py-3 text-gray-700">{item.wilaya}</td>
                  <td className="px-4 py-3 text-gray-500">
                    {item.verificationRequestedAt
                      ? new Date(item.verificationRequestedAt).toLocaleDateString("fr-FR")
                      : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <AdminStatusBadge status={item.verificationStatus} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {item.verificationStatus === "pending" && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1 text-green-700 border-green-300 hover:bg-green-50"
                            disabled={approveMutation.isPending}
                            onClick={() => approveMutation.mutate(item.artisanProfileId)}
                          >
                            <CheckCircle className="h-3.5 w-3.5" />
                            Approuver
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1 text-red-700 border-red-300 hover:bg-red-50"
                            disabled={rejectMutation.isPending}
                            onClick={() => setRejectTarget(item)}
                          >
                            <XCircle className="h-3.5 w-3.5" />
                            Rejeter
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal rejet */}
      <ReasonModal
        open={!!rejectTarget}
        title={`Rejeter la vérification de ${rejectTarget?.fullName ?? ""}`}
        reasonCategory="verification_rejection"
        loading={rejectMutation.isPending}
        onConfirm={(reasonCode, notes) => {
          if (!rejectTarget) return;
          rejectMutation.mutate(
            { artisanProfileId: rejectTarget.artisanProfileId, reasonCode, notes },
            { onSuccess: () => setRejectTarget(null) }
          );
        }}
        onCancel={() => setRejectTarget(null)}
      />
    </div>
  );
}
